import React from "react";
import { Lock, Bot, CalendarCheck, Info, User, Headphones } from "lucide-react";
import { StatusBadge } from "@/components/ds";
import type { InboxMessage } from "@/lib/api";
import { fmtTime, fmtDuration } from "./constants";

function CustomerBubble({ m }: { m: InboxMessage }) {
  return (
    <div className="flex justify-start" data-testid={`msg-customer-${m.id}`}>
      <div className="max-w-[75%] rounded-2xl rounded-tl-sm border border-slate-200 bg-white px-4 py-2.5">
        <div className="mb-0.5 flex items-center gap-1.5"><User className="h-3 w-3 text-slate-400" /><span className="text-[11px] font-semibold text-slate-500">Customer</span></div>
        <p className="whitespace-pre-wrap text-sm text-slate-800">{m.body}</p>
        <p className="mt-1 text-[10px] text-slate-400">{fmtTime(m.created_at)}</p>
      </div>
    </div>
  );
}

function AiBubble({ m }: { m: InboxMessage }) {
  return (
    <div className="flex justify-start" data-testid={`msg-ai-${m.id}`}>
      <div className="max-w-[75%] rounded-2xl rounded-tl-sm border border-blue-200 bg-blue-50 px-4 py-2.5">
        <div className="mb-0.5 flex items-center gap-1.5"><Bot className="h-3 w-3 text-blue-500" /><span className="text-[11px] font-semibold text-blue-600">AI receptionist</span>{m.metadata?.simulated ? <StatusBadge tone="blue">simulated</StatusBadge> : null}</div>
        <p className="whitespace-pre-wrap text-sm text-slate-800">{m.body}</p>
        <p className="mt-1 text-[10px] text-blue-400">{fmtTime(m.created_at)}</p>
      </div>
    </div>
  );
}

function StaffBubble({ m }: { m: InboxMessage }) {
  return (
    <div className="flex justify-end" data-testid={`msg-staff-${m.id}`}>
      <div className="max-w-[75%] rounded-2xl rounded-tr-sm border border-emerald-200 bg-emerald-50 px-4 py-2.5">
        <div className="mb-0.5 flex items-center justify-end gap-1.5"><Headphones className="h-3 w-3 text-emerald-600" /><span className="text-[11px] font-semibold text-emerald-700">Staff</span></div>
        <p className="whitespace-pre-wrap text-sm text-slate-800">{m.body}</p>
        <p className="mt-1 text-right text-[10px] text-emerald-500">{fmtTime(m.created_at)}</p>
      </div>
    </div>
  );
}

function NoteCard({ m }: { m: InboxMessage }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5" data-testid={`msg-note-${m.id}`}>
      <div className="mb-0.5 flex items-center gap-1.5"><Lock className="h-3 w-3 text-amber-600" /><span className="text-[11px] font-semibold text-amber-700">Internal note · not sent to customer</span></div>
      <p className="whitespace-pre-wrap text-sm text-amber-900">{m.body}</p>
      <p className="mt-1 text-[10px] text-amber-500">{fmtTime(m.created_at)}</p>
    </div>
  );
}

function DraftCard({ m }: { m: InboxMessage }) {
  const status = (m.metadata as any)?.send_status;
  return (
    <div className="rounded-lg border border-dashed border-indigo-300 bg-indigo-50/60 px-4 py-2.5" data-testid={`msg-draft-${m.id}`}>
      <div className="mb-0.5 flex items-center gap-1.5"><Bot className="h-3 w-3 text-indigo-600" /><span className="text-[11px] font-semibold text-indigo-700">AI draft · awaiting staff review (not sent)</span></div>
      <p className="whitespace-pre-wrap text-sm text-slate-800">{m.body}</p>
      <p className="mt-1 text-[10px] text-indigo-400">{fmtTime(m.created_at)}{status ? ` · ${status}` : ""}</p>
    </div>
  );
}

function SystemLine({ m }: { m: InboxMessage }) {
  return (
    <div className="flex items-center justify-center gap-1.5 py-1" data-testid={`msg-system-${m.id}`}>
      <Info className="h-3 w-3 text-slate-300" />
      <span className="text-[11px] text-slate-400">{m.body} · {fmtTime(m.created_at)}</span>
    </div>
  );
}

function AiActionLine({ m }: { m: InboxMessage }) {
  return (
    <div className="flex justify-center" data-testid={`msg-aiaction-${m.id}`}>
      <div className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1">
        <Bot className="h-3 w-3 text-indigo-500" />
        <span className="text-[11px] font-medium text-indigo-700">AI action: {m.body}</span>
      </div>
    </div>
  );
}

function AppointmentLine({ m }: { m: InboxMessage }) {
  return (
    <div className="flex justify-center" data-testid={`msg-appointment-${m.id}`}>
      <div className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5">
        <CalendarCheck className="h-3.5 w-3.5 text-emerald-600" />
        <span className="text-xs font-medium text-emerald-800">{m.body}</span>
      </div>
    </div>
  );
}

export function Thread({ messages, callRecord }: { messages: InboxMessage[]; callRecord: Record<string, unknown> | null }) {
  return (
    <div className="space-y-3" data-testid="conversation-thread">
      {callRecord && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" data-testid="call-record-summary">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Headphones className="h-4 w-4 text-slate-400" /> Call summary</div>
          <p className="mt-1 text-xs text-slate-500">
            {String(callRecord.direction ?? "inbound")} · {fmtDuration(callRecord.duration_seconds as number)} · outcome: {String(callRecord.outcome ?? "—")}
          </p>
          <p className="mt-1 text-[11px] text-slate-400">Transcript segments appear below.</p>
        </div>
      )}
      {messages.map((m) => {
        switch (m.kind) {
          case "customer_message": return <CustomerBubble key={m.id} m={m} />;
          case "ai_reply": return <AiBubble key={m.id} m={m} />;
          case "staff_reply": return <StaffBubble key={m.id} m={m} />;
          case "internal_note": return <NoteCard key={m.id} m={m} />;
          case "ai_draft": return <DraftCard key={m.id} m={m} />;
          case "ai_action": return <AiActionLine key={m.id} m={m} />;
          case "appointment_event": return <AppointmentLine key={m.id} m={m} />;
          default: return <SystemLine key={m.id} m={m} />;
        }
      })}
    </div>
  );
}
